import React, { useState, useEffect } from 'react';
import { triggerComparisons, aggFunctionOptions } from './constants'
import { Box, Grid, List, ListItem, Paper, Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography, } from '@mui/material';
import { format } from 'date-fns';

const QueryAuditHistory = (props) => {
    const { trigger } = props;
  const [rowCount, setRowCount] = useState(0);

  const formatAuditRow = (audit) => {
    const type = String(audit.type).replace('Trigger', 'Query');
    const isUpdate = audit.event === 'updated';
    const labelMap = type === 'Query'
      ? { name: 'Name' }
      : {
          agg_function: 'Aggregate By',
          agg_value: 'Count of',
          data_input: 'For Field',
          agg_days: 'Count # Days',
          value: 'Value',
        };
  
    const { oldValues, newValues } = formatValueChanges(
      labelMap,
      audit['old_values'],
      audit['new_values'],
      audit['auditable'],
      isUpdate
    );
  
    const action = `${type} ${audit.event}`;
  
    const changesContent = (
        <Box>
        {oldValues.length > 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1">
                Original Values:
              </Typography>
              <List>
                {oldValues?.map((value, index) => (
                  <ListItem key={`${index}_${value}`}>
                    <Typography className='text-transform-capitalize'>{value}</Typography>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        )}
        {newValues.length > 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1">
                New Values:
              </Typography>
              <List>
                {newValues?.map((value, index) => (
                  <ListItem key={`${index}_${value}`}>
                    <Typography className='text-transform-capitalize'>{value}</Typography>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        )}
      </Box>
    );
    const user = audit['created_by'];
    const ip = audit['ip_address'];
  
    const tz = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = format(new Date(audit['created_at']), 'yyyy-MM-dd, h:mm:ss a zzz', { timeZone: tz });
  
    return (
      <TableRow key={audit.id} className="bg-gray-50 no-border">
        <TableCell className='table-row-data no-border'>{action}</TableCell>
        <TableCell className='table-row-data no-border'>{changesContent}</TableCell>
        <TableCell className='table-row-data no-border'>{user}</TableCell>
        <TableCell className='table-row-data no-border'>{ip}</TableCell>
        <TableCell className='table-row-data no-border'>{date}</TableCell>
      </TableRow>
    );
  };
  

  const formatValueChanges = (keys, oldValues, newValues, record, isUpdate) => {
    let comparison = {
        oldValues: [],
        newValues: [],
    };

    for (const [key, label] of Object.entries(keys)) {
        let oldValue = oldValues.hasOwnProperty(key) ? oldValues[key] : record[key];
        let newValue = newValues.hasOwnProperty(key) ? newValues[key] : record[key];

        if (key === 'operator') {
            oldValue = oldValue ? formatOperator(oldValue) : null;
            newValue = newValue ? formatOperator(newValue) : null;
        }

        if (key === 'agg_function') {
            oldValue = oldValue ? formatAggFunction(oldValue) : null;
            newValue = newValue ? formatAggFunction(newValue) : null;
        }

        if (isUpdate && oldValue !== null) {
            comparison.oldValues.push(`${label}: ${oldValue}`);
        }

        if (newValue !== null) {
            comparison.newValues.push(`${label}: ${newValue}`);
        }
    }

    return comparison;
  };

  const formatAggFunction = (aggFunction) => {
    const index = aggFunctionOptions.findIndex(option => {
        return option.value === aggFunction;
    });

    return aggFunctionOptions[index].label;
  };

  const formatOperator = (operatorValue) => {
    const index = triggerComparisons.findIndex(operator => {
        return operator.value === operatorValue;
    });

    return triggerComparisons[index].label;
  };

  useEffect(() => {
    let audits = [];
    if (trigger && trigger.definitions) {
      trigger.definitions.forEach((definition) => {
        if (definition.audits) {
          definition.audits.forEach((audit) => {
            const modifiedAudit = {
              ...audit,
              created_by: audit && audit.user ? `${audit?.user?.first_name} ${audit?.user?.last_name}` : '-',
            };
            audits.push(modifiedAudit);
          });
        }
      });
    }
    if (trigger && trigger.audits) {
      trigger.audits.forEach((audit) => {
        const modifiedAudit = {
          ...audit,
          created_by: audit && audit.user ? `${audit?.user?.first_name} ${audit?.user?.last_name}` : '-',
        };
        audits.push(modifiedAudit);
      });
    }
  
    const sortedAudits = audits.sort((a, b) => (a.id > b.id ? 1 : -1));
    setRowCount(sortedAudits.length);
  }, [trigger]);
  
  const renderTriggerAudits = () => {
    let audits = [];
    if (trigger && trigger.definitions) {
      trigger.definitions.forEach((definition) => {
        if (definition.audits) {
          definition.audits.forEach((audit) => {
            const modifiedAudit = {
              ...audit,
              created_by: audit && audit.user ? `${audit?.user?.first_name} ${audit?.user?.last_name}` : '-',
            };
            audits.push(modifiedAudit);
          });
        }
      });
    }
    if (trigger && trigger.audits) {
      trigger.audits.forEach((audit) => {
        const modifiedAudit = {
          ...audit,
          created_by: audit && audit.user ? `${audit?.user?.first_name} ${audit?.user?.last_name}` : '-',
        };
        audits.push(modifiedAudit);
      });
    }
  
    const sortedAudits = audits.sort((a, b) => (a.id > b.id ? 1 : -1));
    return sortedAudits?.map((audit) => formatAuditRow(audit));
  };
  
  

  return (
    <TableContainer component={Paper} className='no-box-shadow no-border'>
      <Table className='table-border-collapse no-border'>
        <TableHead className='queries-table-heading'
          sx={{
            '& .MuiTableCell-root': {
              borderBottom: 'none',
            },
          }}>
          <TableRow className="bg-white no-border">
            <TableCell className="table-headers-title">Action</TableCell>
            <TableCell className="table-headers-title">Changes</TableCell>
            <TableCell className="table-headers-title">User</TableCell>
            <TableCell className="table-headers-title">IP</TableCell>
            <TableCell className="table-headers-title">Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderTriggerAudits()}</TableBody>
      </Table>
    </TableContainer>
  );
};

export default QueryAuditHistory;
